import React, { useEffect, useState, useMemo } from "react";
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, Spinner, FormGroup, Label, Input, FormFeedback, Row, Col, Alert,
} from "reactstrap";
import Select from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { adminGetPlans, adminAssignSubscription, adminGetUpgradePreview, getCompanySubscriptionHistory } from "../../../api/requests/subscription-requests";
import DateInput from "../../../components/Common/DateInput";
import { formatLicenseDate } from "../../../utils/subscriptionDates";

const NAVY = "#221C5D";

/**
 * Manuel abonelik tanımlama modalı (SUPER_ADMIN)
 *
 * Props:
 * @param {boolean}  isOpen
 * @param {Function} toggle
 * @param {Object}   company   - { id, companyName }
 * @param {Function} onSuccess - Başarılı tanımlamadan sonra çağrılır
 */
export const AssignSubscriptionModal = ({ isOpen, toggle, company, onSuccess }) => {
    const [plans, setPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [saving, setSaving] = useState(false);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [loadingCurrent, setLoadingCurrent] = useState(false);
    const [upgradePreview, setUpgradePreview] = useState(null);

    const today = new Date().toISOString().slice(0, 10);

    useEffect(() => {
        if (isOpen && company?.id) {
            setLoadingPlans(true);
            adminGetPlans()
                .then((res) => setPlans(res.data || []))
                .catch(() => toast.error("Paketler yüklenemedi"))
                .finally(() => setLoadingPlans(false));

            setLoadingCurrent(true);
            getCompanySubscriptionHistory(company.id)
                .then((res) => {
                    // İleri tarihli dönem "Planlandı" gelir; mevcut paket yalnız başlamış ACTIVE kayıttır.
                    const subs = res.data || [];
                    const active = subs.find(s => s.status === "ACTIVE" && (!s.startDate || s.startDate <= today));
                    setCurrentSubscription(active || null);
                })
                .catch(() => setCurrentSubscription(null))
                .finally(() => setLoadingCurrent(false));
        }
    }, [isOpen, company?.id, today]);

    const planOptions = plans.map((p) => ({ value: p.id, label: p.name, sortOrder: p.sortOrder }));

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            planId: "",
            period: "NEW_PERIOD",
            startDate: today,
            endDate: "",
            reason: "",
        },
        validationSchema: Yup.object({
            planId: Yup.mixed().required("Paket seçimi gerekli"),
            startDate: Yup.string().required("Başlangıç tarihi gerekli"),
            reason: Yup.string().required("Sebep gerekli"),
        }),
        onSubmit: async (values) => {
            if (!company?.id) return;
            setSaving(true);
            try {
                const newPeriod = values.period === "NEW_PERIOD";
                await adminAssignSubscription({
                    companyId: company.id,
                    planId: values.planId,
                    period: values.period,
                    startDate: newPeriod ? values.startDate : null,
                    endDate: newPeriod ? values.endDate || null : null,
                    reason: values.reason,
                });
                toast.success("Abonelik başarıyla tanımlandı");
                formik.resetForm();
                setCurrentSubscription(null);
                toggle();
                if (onSuccess) onSuccess();
            } catch (err) {
                toast.error(err.message);
            } finally {
                setSaving(false);
            }
        },
    });

    const selectedPlan = useMemo(() => {
        if (!formik.values.planId) return null;
        return plans.find(p => String(p.id) === String(formik.values.planId));
    }, [formik.values.planId, plans]);

    const currentPlanObj = useMemo(() => {
        if (!currentSubscription?.planName) return null;
        return plans.find(p => p.name === currentSubscription.planName);
    }, [currentSubscription, plans]);

    const isDowngrade = selectedPlan && currentPlanObj && selectedPlan.sortOrder < currentPlanObj.sortOrder;
    const isSamePlan = selectedPlan && currentPlanObj && String(selectedPlan.id) === String(currentPlanObj.id);
    // Süresiz (Ücretsiz) pakette kalan süre yoktur: yeni paket her zaman sıfırdan başlar.
    const canUseRemaining = !!currentSubscription && !currentPlanObj?.unlimitedDuration
        && !!currentSubscription.endDate && currentSubscription.endDate > today;
    const period = canUseRemaining ? formik.values.period : "NEW_PERIOD";

    useEffect(() => {
        setUpgradePreview(null);
        if (!company?.id || !selectedPlan || !canUseRemaining || isSamePlan) return undefined;
        let cancelled = false;
        adminGetUpgradePreview(company.id, selectedPlan.id)
            .then((data) => { if (!cancelled) setUpgradePreview(data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [company?.id, selectedPlan, canUseRemaining, isSamePlan]);

    const formatPrice = (value) =>
        new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0) + " ₺";

    const handleClose = () => {
        formik.resetForm();
        setCurrentSubscription(null);
        toggle();
    };

    if (!company) return null;

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered>
            <ModalHeader toggle={handleClose} className="border-bottom">
                <div className="d-flex align-items-center gap-2" style={{ color: NAVY }}>
                    <i className="bx bx-package fs-4" />
                    Manuel Abonelik Tanımla
                </div>
            </ModalHeader>

            <ModalBody className="py-4">
                {/* Firma bilgisi */}
                <div className="border rounded p-3 bg-light mb-3">
                    <div className="d-flex align-items-center gap-2">
                        <i className="bx bx-building-house text-muted" style={{ fontSize: 20 }} />
                        <div>
                            <div className="fw-semibold text-dark">{company.companyName}</div>
                            <div className="text-muted small">Ödeme almadan abonelik tanımlanacak</div>
                        </div>
                    </div>
                </div>

                {/* Mevcut abonelik bilgisi */}
                {loadingCurrent ? (
                    <div className="text-center py-2 mb-3"><Spinner size="sm" color="secondary" /></div>
                ) : currentSubscription && (
                    <div className="border rounded p-3 mb-3" style={{ backgroundColor: "#f0f4ff" }}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <i className="bx bx-info-circle" style={{ color: NAVY }} />
                            <span className="fw-semibold small" style={{ color: NAVY }}>Mevcut Abonelik</span>
                        </div>
                        <div className="small text-dark">
                            <strong>{currentSubscription.planName}</strong>
                            <span className="text-muted ms-2">
                                ({currentSubscription.status === "TRIAL" ? "Deneme" : currentSubscription.status === "PENDING" ? "Ödeme Bekliyor" : "Aktif"})
                            </span>
                        </div>
                        {currentSubscription.endDate && (
                            <div className="text-muted" style={{ fontSize: ".75rem" }}>
                                Bitiş: {formatLicenseDate(currentSubscription.endDate)}
                            </div>
                        )}
                        <div className="text-muted mt-1" style={{ fontSize: ".75rem" }}>
                            Yeni paket tanımlandığında bu abonelik ve varsa bekleyen paket düşürmesi otomatik olarak sonlandırılacaktır.
                        </div>
                    </div>
                )}

                <FormGroup>
                    <Label className="small fw-medium">Paket</Label>
                    {loadingPlans ? (
                        <div className="text-center py-2"><Spinner size="sm" color="primary" /></div>
                    ) : (
                        <Select
                            options={planOptions}
                            value={planOptions.find((o) => String(o.value) === String(formik.values.planId)) || null}
                            onChange={(opt) => { formik.setFieldValue("planId", opt?.value ?? ""); formik.setFieldTouched("planId", true, false); }}
                            placeholder="Paket seçin..."
                            isClearable
                            noOptionsMessage={() => "Paket bulunamadı"}
                        />
                    )}
                    {formik.touched.planId && formik.errors.planId && (
                        <div className="text-danger small mt-1">{formik.errors.planId}</div>
                    )}
                </FormGroup>

                {/* Downgrade uyarısı */}
                {isDowngrade && (
                    <Alert color="warning" className="d-flex align-items-start gap-2 py-2">
                        <i className="bx bx-error-circle mt-1" />
                        <div className="small">
                            <strong>Düşürme işlemi:</strong> {currentPlanObj.name} → {selectedPlan.name}.
                            Firmanın mevcut kullanım limitleri yeni paketin limitlerine göre kısıtlanacaktır.
                        </div>
                    </Alert>
                )}

                {/* Aynı paket uyarısı */}
                {isSamePlan && (
                    <Alert color="info" className="d-flex align-items-start gap-2 py-2">
                        <i className="bx bx-info-circle mt-1" />
                        <div className="small">
                            Firma zaten bu pakette. Süre uzatmak istiyorsanız bitiş tarihini değiştirebilirsiniz.
                        </div>
                    </Alert>
                )}

                {canUseRemaining && selectedPlan && !isSamePlan && (
                    <FormGroup tag="fieldset">
                        <Label className="small fw-medium">Süre</Label>
                        <FormGroup check>
                            <Input
                                type="radio"
                                id="period-remaining"
                                checked={period === "REMAINING_PERIOD"}
                                onChange={() => formik.setFieldValue("period", "REMAINING_PERIOD")}
                            />
                            <Label check htmlFor="period-remaining" className="small">
                                Kalan sürede kullan — bugün başlar, {formatLicenseDate(currentSubscription.endDate)} tarihinde biter
                                {upgradePreview?.immediateOption && (
                                    <div className="text-muted" style={{ fontSize: ".75rem" }}>
                                        Firma kendisi alsaydı: {formatPrice(upgradePreview.immediateOption.totalAmount)}
                                    </div>
                                )}
                            </Label>
                        </FormGroup>
                        <FormGroup check>
                            <Input
                                type="radio"
                                id="period-new"
                                checked={period === "NEW_PERIOD"}
                                onChange={() => formik.setFieldValue("period", "NEW_PERIOD")}
                            />
                            <Label check htmlFor="period-new" className="small">
                                Yeni dönem — paketin tam süresi, tarihler aşağıdan değiştirilebilir
                                {upgradePreview?.extensionOption && (
                                    <div className="text-muted" style={{ fontSize: ".75rem" }}>
                                        Firma kendisi alsaydı: {formatPrice(upgradePreview.extensionOption.totalAmount)}
                                        {upgradePreview.extensionOption.currentPlanCredit > 0 &&
                                            ` (mevcut paketin kalan değeri ${formatPrice(upgradePreview.extensionOption.currentPlanCredit)} düşülmüş)`}
                                    </div>
                                )}
                            </Label>
                        </FormGroup>
                    </FormGroup>
                )}

                {period === "NEW_PERIOD" && (
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label className="small fw-medium">Başlangıç Tarihi</Label>
                            <DateInput
                                value={formik.values.startDate}
                                onChange={(iso) => formik.setFieldValue("startDate", iso)}
                            />
                            <FormFeedback>{formik.errors.startDate}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label className="small fw-medium">Bitiş Tarihi <span className="text-muted">(opsiyonel)</span></Label>
                            <DateInput
                                value={formik.values.endDate}
                                onChange={(iso) => formik.setFieldValue("endDate", iso)}
                            />
                            <div className="text-muted" style={{ fontSize: ".75rem" }}>Boş bırakılırsa plan süresine göre hesaplanır</div>
                        </FormGroup>
                    </Col>
                </Row>
                )}

                <FormGroup>
                    <Label className="small fw-medium">Sebep</Label>
                    <Input
                        type="textarea"
                        name="reason"
                        rows={2}
                        value={formik.values.reason}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.reason && !!formik.errors.reason}
                        placeholder="Özel anlaşmaya istinaden..."
                    />
                    <FormFeedback>{formik.errors.reason}</FormFeedback>
                </FormGroup>
            </ModalBody>

            <ModalFooter className="border-top">
                <div className="d-flex justify-content-end gap-2 w-100">
                    <Button color="secondary" onClick={handleClose} disabled={saving} className="px-4">
                        İptal
                    </Button>
                    <Button
                        style={{ backgroundColor: NAVY, borderColor: NAVY }}
                        onClick={formik.handleSubmit}
                        disabled={saving || isSamePlan}
                        className="px-4 d-flex align-items-center gap-2"
                    >
                        {saving ? (
                            <><Spinner size="sm" /> Tanımlanıyor...</>
                        ) : (
                            <><i className="bx bx-check" /> Tanımla</>
                        )}
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};
